import { store } from "@/store";
import { loginRouteName, notFoundRouteName } from "@/router/defaultRoutes";
import { NavigationGuardWithThis } from "vue-router";

// localStorage에서 메뉴 데이터 가져오기
const getMenuData = () => {
  try {
    // 1. localStorage에서 menu 확인
    const menuData = localStorage.getItem("menu");
    if (menuData) {
      const parsed = JSON.parse(menuData);
      if (parsed && parsed.length > 0) return parsed;
    }

    // 2. localStorage에서 menuData 확인
    const menuDataAlt = localStorage.getItem("menuData");
    if (menuDataAlt) {
      const parsed = JSON.parse(menuDataAlt);
      if (parsed && parsed.length > 0) return parsed;
    }

    // 3. store에서 메뉴 데이터 확인
    try {
      const storeMenu = store.getters.menu || store.getters.menuData;
      if (storeMenu && storeMenu.length > 0) {
        return storeMenu;
      }
    } catch (error) {
      console.warn("store에서 메뉴 데이터 접근 실패:", error);
    }

    // 4. sessionStorage에서 확인
    const sessionMenu = sessionStorage.getItem("menu");
    if (sessionMenu) {
      const parsed = JSON.parse(sessionMenu);
      if (parsed && parsed.length > 0) return parsed;
    }

    return [];
  } catch (error) {
    console.error("❌ 메뉴 데이터 파싱 오류:", error);
    return [];
  }
};

// 사용자의 역할 가져오기 (localStorage 또는 store에서)
const getUserRole = () => {
  // 1. localStorage에서 userRole 확인
  const userRole = localStorage.getItem("userRole");
  if (userRole) {
    return userRole;
  }

  // 2. store에서 역할 확인
  try {
    const storeRole = store.getters.userRole;
    if (storeRole) {
      return storeRole;
    }
  } catch (error) {
    console.warn("store에서 역할 접근 실패:", error);
  }

  // 3. sessionStorage에서 확인
  const sessionRole = sessionStorage.getItem("userRole");
  if (sessionRole) {
    return sessionRole;
  }

  return "USER";
};

// 특정 URL에 대한 접근 권한 확인
const checkUrlAccess = (url: string, userRole: string, routeMeta?: any, isAuthenticated: boolean = false) => {
  const menuList = getMenuData();

  // 로그인 관련 URL은 항상 접근 가능
  const publicUrls = ["/", "/login", "/signup", "/404"];
  if (publicUrls.includes(url)) {
    return true;
  }

  // 🔴 인증된 사용자는 기본적으로 모든 페이지 접근 허용 (권한 체크 우회)
  if (isAuthenticated) {
    return true;
  }

  // 1. 라우트의 meta.permission 먼저 체크 (우선순위 높음)
  if (routeMeta?.permission) {
    const routePermissions = routeMeta.permission.split(",").map((p: string) => p.trim());
    const hasRoutePermission = routePermissions.includes(userRole);

    if (!hasRoutePermission) {
      return false;
    }
  }

  // 2. 메뉴 리스트에서 해당 URL 찾기 (더 정확한 매칭)
  const menuItem = menuList.find((menu: any) => {
    const menuAddress = menu.menuAddress || "";
    const menuUrl = menu.menuUrl || "";
    const menuPath = menu.menuPath || "";

    // 정확한 경로 매칭
    const exactMatch = menuAddress === url || menuUrl === url || menuPath === url;

    // 부분 경로 매칭 (예: /cleaning-robot이 /cleaning-robot과 매칭)
    const partialMatch = url.startsWith(menuAddress) || url.startsWith(menuUrl) || url.startsWith(menuPath);

    // 메뉴 이름 기반 매칭 (예: cleaning-robot이 cleaning-robot과 매칭)
    const nameMatch = menu.menuName && url.includes(menu.menuName.toLowerCase().replace(/\s+/g, "-"));

    const isMatch = exactMatch || partialMatch || nameMatch;

    if (isMatch) {
    }

    return isMatch;
  });

  // 메뉴 자체가 존재하지 않으면 접근 거부
  if (!menuItem) {
    return false;
  }

  // 3. 메뉴의 availableRoleList 체크
  if (menuItem.availableRoleList) {
    const allowedRoles = menuItem.availableRoleList.split(",").map(role => role.trim());
    const hasRole = allowedRoles.includes(userRole);

    if (!hasRole) {
    }

    return hasRole;
  }

  // availableRoleList가 없으면 기본적으로 접근 거부
  return false;
};

// 로그인 체크
export const checkAuthenticate: NavigationGuardWithThis<undefined> = (to, from, next) => {
  // 🔴 스토어가 초기화되지 않은 경우 안전하게 처리
  if (!store || !store.getters) {
    return next("/login");
  }

  // 🔴 무한 루프 방지를 위한 안전장치
  const maxRetries = 3;
  let retryCount = 0;

  const safeNext = (path?: string | { path?: string; name?: string }) => {
    if (retryCount >= maxRetries) {
      console.error("❌ 최대 재시도 횟수 초과, 로그인 페이지로 강제 이동");
      localStorage.clear();
      sessionStorage.clear();
      return next("/login");
    }
    retryCount++;
    if (typeof path === "string") {
      next(path);
    } else if (path && (path.path || path.name)) {
      // Handle object path with explicit type casting
      if (path.path) {
        next(path.path);
      } else if (path.name) {
        next({ name: path.name });
      } else {
        next();
      }
    } else {
      next();
    }
  };

  // 🔴 초기 인증 상태 확인
  let isAuthenticated = store.getters.isAuthenticated;

  // 🔴 로그인 페이지 접근 시 세션 상태 확인
  if (to.path === "/login") {
    // 세션 데이터가 있는지 확인
    const hasLoginSession = localStorage.getItem("login-session");
    const hasTokenStatus = sessionStorage.getItem("token-status");
    const hasJWT = store.getters.JWT;

    // 유효한 세션이 있으면 메인페이지로 리다이렉트
    if (hasTokenStatus && hasJWT) {
      return safeNext({ path: "/home" });
    } else {
      return safeNext();
    }
  }

  // store에서 인증 상태가 false이지만 localStorage에 유효한 세션이 있는 경우
  if (!isAuthenticated) {
    try {
      const savedSession = localStorage.getItem("login-session");

      if (savedSession) {
        const sessionData = JSON.parse(savedSession);

        if (sessionData.state === "LoggedIn" && sessionData.accessToken) {
          // store에 즉시 복원
          store.commit("restoreLoginSession", sessionData);

          // 메뉴 데이터도 복원
          const savedMenu = localStorage.getItem("menu");
          if (savedMenu) {
            try {
              const menuData = JSON.parse(savedMenu);
              if (menuData && menuData.length > 0) {
                store.commit("menu", menuData);
              }
            } catch (error) {
              console.warn("메뉴 데이터 복원 실패:", error);
            }
          }

          isAuthenticated = true;
        } else if (sessionData.state === "anonymous" || !sessionData.accessToken) {
          // 유효하지 않은 세션 데이터 정리
          localStorage.removeItem("login-session");
          localStorage.removeItem("menu");
        }
      }
    } catch (error) {
      console.error("라우터 가드에서 세션 복원 실패:", error);
      // 손상된 세션 데이터 정리
      localStorage.removeItem("login-session");
      localStorage.removeItem("menu");
    }
  }

  // 페이지 메타 태그에서 체크
  const isAnonymousGrant = to.meta?.grant === "anonymous";

  // 사용자 역할 가져오기
  const userRole = getUserRole();

  // localStorage 메뉴 데이터 기반 권한 체크
  const hasAuthorization = checkUrlAccess(to.path, userRole, to.meta, isAuthenticated);

  // 🔴 인증된 사용자가 로그인 페이지에 접근하려고 하면 홈으로 리다이렉트
  if (isAuthenticated && to.path === "/login") {
    return safeNext({ path: "/home" });
  }

  // 🔴 로그인 페이지로 이동하려는 경우 모든 세션 데이터 강제 정리
  if (to.path === "/login" && isAuthenticated) {
    localStorage.removeItem("menu");
    localStorage.removeItem("current_url");
    localStorage.removeItem("original_url");
    sessionStorage.removeItem("token-status");

    // 스토어 상태도 강제 초기화
    if (store && store.commit) {
      store.commit("setLogoutState");
    }

    // 인증 상태 강제 해제
    isAuthenticated = false;
  }

  // 🔴 새로고침 시 현재 페이지에 그대로 머물러 있도록 허용
  const currentUrl = window.location.pathname;
  const isRefresh = from?.path === "/" || from?.path === undefined;

  if (isAuthenticated && isRefresh) {
    // 새로고침 시 현재 URL과 다르면 현재 URL로 강제 이동
    if (to.path !== currentUrl) {
      return safeNext(currentUrl);
    }

    return safeNext();
  }

  // 🔴 현재 URL을 localStorage에 저장 (새로고침 시 복원용)
  if (isAuthenticated && to.path !== "/login" && to.path !== "/select-site") {
    // 원래 페이지 URL을 별도로 저장 (리다이렉트 전)
    if (to.path !== "/home") {
      localStorage.setItem("original_url", to.path);
    }
    localStorage.setItem("current_url", to.path);
  }

  // 로그인 창 등 익명접근이 가능하거나 로그인 했으면 페이지 이동
  if (isAnonymousGrant) {
    return safeNext();
  } else if (isAuthenticated) {
    // 🔴 인증된 사용자는 모든 페이지 접근 허용 (권한 체크 우회)
    return safeNext();
  } else {
    // 인증이 없으면 로그인 창으로 이동
    return safeNext({
      name: loginRouteName,
    });
  }
};
